import Plugin from "@ckeditor/ckeditor5-core/src/plugin"
import InsertImageWithoutIsbnCommand from "./insertimagewithoutisbncommand"
import { enablePlaceholder } from "@ckeditor/ckeditor5-engine/src/view/placeholder"
import {
  toWidget,
  toWidgetEditable,
} from "@ckeditor/ckeditor5-widget/src/utils"
import Widget from "@ckeditor/ckeditor5-widget/src/widget"

export default class InsertImageWithoutIsbnEditing extends Plugin {
  static get requires() {
    return [Widget]
  }

  init() {
    const editor = this.editor

    this._defineSchema()
    this._defineConverters()

    this.editor.commands.add(
      "imageInsertWithoutIsbn",
      new InsertImageWithoutIsbnCommand(editor)
    )
  }

  afterInit() {
    const editor = this.editor
    const schema = editor.model.schema

    // 'image'モデルはすでにckeditor5-imageによって定義済みなので、そのinit処理が完了してからでないと、
    // エラーが発生してしまうため、afterInit()にてそのスキーマ定義を拡張する
    schema.extend("image", {
      allowIn: "book-box",
      isObject: true,
      allowContentOf: "$block",
    })

    /**
     * <figure>タグにclass属性を追加するための設定
     * cf. https://ckeditor.com/docs/ckeditor5/latest/framework/guides/deep-dive/conversion/conversion-preserving-custom-content.html#adding-extra-attributes-to-elements-contained-in-a-figure
     * viewへの変換時のみclass属性を付ければいいので、downcastの時だけでいいはず
     */
    editor.conversion.for("downcast").add(
      (dispatcher) =>
        dispatcher.on("insert:image", (evt, data, conversionApi) => {
          const modelElement = data.item
          const viewFigure = conversionApi.mapper.toViewElement(modelElement)

          if (!viewFigure) {
            return
          }

          const ancestor = viewFigure.findAncestor("blockquote")
          if (
            ancestor &&
            ancestor.hasClass("honcierge-book") &&
            ancestor.hasClass("without-isbn")
          ) {
            conversionApi.writer.addClass(
              ["book-image", "box-element"],
              viewFigure
            )
          }
        }),
      { priority: "low" }
    )
  }

  /**
   * <blockquote class="honcierge-book without-isbn" >
   *   <article class="container">
   *     <section class="book-box">
   *       <div class="book-content box-element">
   *         <h1 class="book-title"></h1>
   *         <div class="book-published"></div>
   *         <dl class="book-meta">
   *           <dt class="author"></dt>
   *           <dd class="publisher"></dd>
   *         </dl>
   *       </div>
   *       <figure class="book-image box-element">
   *         <img src="https://m.media-amazon.com/images/I/61HZk8eh97L.jpg">
   *       </figure>
   *     </section>
   *   </article>
   * </blockquote>
   */
  _defineSchema() {
    const schema = this.editor.model.schema

    schema.register("honcierge-book-without-isbn", {
      isObject: true,
      allowWhere: "$block",
    })
    schema.register("article-container", {
      allowIn: "honcierge-book-without-isbn",
      allowContentOf: "$block",
    })
    schema.register("book-box", {
      allowIn: "article-container",
      allowContentOf: "$block",
    })
    schema.register("book-content", {
      allowIn: "book-box",
      allowContentOf: "$block",
    })
    schema.register("book-title", {
      allowIn: "book-content",
      isLimit: true,
      allowContentOf: "$block",
    })
    schema.register("book-published", {
      allowIn: "book-content",
      isLimit: true,
      allowContentOf: "$block",
    })
    schema.register("book-meta", {
      allowIn: "book-content",
      isObject: true,
      allowContentOf: "$block",
    })
    schema.register("author", {
      allowIn: "book-meta",
      isLimit: true,
      allowContentOf: "$block",
    })
    schema.register("publisher", {
      allowIn: "book-meta",
      isLimit: true,
      allowContentOf: "$block",
    })
    // 画像(image)に関するスキーマはすでに定義済みなため、afterInit()にて定義を拡張する
  }

  _defineConverters() {
    const conversion = this.editor.conversion
    this._convertHonciergeBook(conversion)
    this._convertArticleContainer(conversion)
    this._convertBookBox(conversion)
    this._convertBookContent(conversion)
    this._convertBookTitle(conversion)
    this._convertBookPublished(conversion)
    this._convertBookMeta(conversion)
    this._convertAuthor(conversion)
    this._convertPublisher(conversion)
  }

  // <blockquote class="honcierge-book without-isbn">
  _convertHonciergeBook(conversion) {
    const tagName = "blockquote"
    const modelName = "honcierge-book-without-isbn"
    const classes = ["honcierge-book", "without-isbn"]
    conversion.for("upcast").elementToElement({
      model: modelName,
      view: {
        name: tagName,
        classes: classes,
      },
    })
    conversion.for("dataDowncast").elementToElement({
      model: modelName,
      view: {
        name: tagName,
        classes: classes,
      },
    })
    conversion.for("editingDowncast").elementToElement({
      model: modelName,
      view: (modelElement, viewWriter) => {
        const element = viewWriter.createContainerElement(tagName, {
          class: classes.join(" "),
        })

        return toWidget(element, viewWriter)
      },
    })
  }

  // <article class="container">
  _convertArticleContainer(conversion) {
    const tagName = "article"
    const modelName = "article-container"
    const classes = ["container"]
    conversion.for("upcast").elementToElement({
      model: modelName,
      view: {
        name: tagName,
        classes: classes,
      },
    })
    conversion.for("dataDowncast").elementToElement({
      model: modelName,
      view: {
        name: tagName,
        classes: classes,
      },
    })
    conversion.for("editingDowncast").elementToElement({
      model: modelName,
      view: (modelElement, viewWriter) => {
        const element = viewWriter.createContainerElement(tagName, {
          class: classes.join(" "),
        })
        return toWidget(element, viewWriter)
      },
    })
  }

  // <section class="book-box">
  _convertBookBox(conversion) {
    const tagName = "section"
    const modelName = "book-box"
    const classes = ["book-box"]
    conversion.for("upcast").elementToElement({
      model: modelName,
      view: {
        name: tagName,
        classes: classes,
      },
    })
    conversion.for("dataDowncast").elementToElement({
      model: modelName,
      view: {
        name: tagName,
        classes: classes,
      },
    })
    conversion.for("editingDowncast").elementToElement({
      model: modelName,
      view: (modelElement, viewWriter) => {
        const element = viewWriter.createContainerElement(tagName, {
          class: classes.join(" "),
        })
        return toWidget(element, viewWriter)
      },
    })
  }

  // <div class="book-content box-element">
  _convertBookContent(conversion) {
    const tagName = "div"
    const modelName = "book-content"
    const classes = ["book-content", "box-element"]
    conversion.for("upcast").elementToElement({
      model: modelName,
      view: {
        name: tagName,
        classes: classes,
      },
    })
    conversion.for("dataDowncast").elementToElement({
      model: modelName,
      view: {
        name: tagName,
        classes: classes,
      },
    })
    conversion.for("editingDowncast").elementToElement({
      model: modelName,
      view: (modelElement, viewWriter) => {
        const element = viewWriter.createContainerElement(tagName, {
          class: classes.join(" "),
        })
        return toWidget(element, viewWriter)
      },
    })
  }

  // <h1 class="book-title"></h1>
  _convertBookTitle(conversion) {
    const tagName = "h1"
    const modelName = "book-title"
    const classes = [modelName]
    conversion.for("upcast").elementToElement({
      model: modelName,
      view: {
        name: tagName,
        classes: classes,
      },
    })
    conversion.for("dataDowncast").elementToElement({
      model: modelName,
      view: {
        name: tagName,
        classes: classes,
      },
    })
    conversion.for("editingDowncast").elementToElement({
      model: modelName,
      view: (modelElement, viewWriter) => {
        const element = viewWriter.createEditableElement(tagName, {
          class: classes.join(" "),
        })
        enablePlaceholder({
          view: this.editor.editing.view,
          element: element,
          text: "本のタイトル(必須)",
        })
        return toWidgetEditable(element, viewWriter)
      },
    })
  }

  //  <div class="book-published"></div>
  _convertBookPublished(conversion) {
    const tagName = "div"
    const modelName = "book-published"
    const classes = [modelName]
    conversion.for("upcast").elementToElement({
      model: modelName,
      view: {
        name: tagName,
        classes: classes,
      },
    })
    conversion.for("dataDowncast").elementToElement({
      model: modelName,
      view: {
        name: tagName,
        classes: classes,
      },
    })
    conversion.for("editingDowncast").elementToElement({
      model: modelName,
      view: (modelElement, viewWriter) => {
        const element = viewWriter.createEditableElement(tagName, {
          class: classes.join(" "),
        })
        enablePlaceholder({
          view: this.editor.editing.view,
          element: element,
          text: "出版日",
        })
        return toWidgetEditable(element, viewWriter)
      },
    })
  }

  _convertBookMeta(conversion) {
    const tagName = "dl"
    const modelName = "book-meta"
    const classes = [modelName]
    conversion.for("upcast").elementToElement({
      model: modelName,
      view: {
        name: tagName,
        classes: classes,
      },
    })
    conversion.for("dataDowncast").elementToElement({
      model: modelName,
      view: {
        name: tagName,
        classes: classes,
      },
    })
    conversion.for("editingDowncast").elementToElement({
      model: modelName,
      view: (modelElement, viewWriter) => {
        const element = viewWriter.createContainerElement(tagName, {
          class: classes.join(" "),
        })
        return toWidget(element, viewWriter)
      },
    })
  }

  _convertAuthor(conversion) {
    const tagName = "dt"
    const modelName = "author"
    const classes = [modelName]
    conversion.for("upcast").elementToElement({
      model: modelName,
      view: {
        name: tagName,
        classes: classes,
      },
    })
    conversion.for("dataDowncast").elementToElement({
      model: modelName,
      view: {
        name: tagName,
        classes: classes,
      },
    })
    conversion.for("editingDowncast").elementToElement({
      model: modelName,
      view: (modelElement, viewWriter) => {
        const element = viewWriter.createEditableElement(tagName, {
          class: classes.join(" "),
        })
        enablePlaceholder({
          view: this.editor.editing.view,
          element: element,
          text: "著者",
        })
        return toWidgetEditable(element, viewWriter)
      },
    })
  }

  _convertPublisher(conversion) {
    const tagName = "dd"
    const modelName = "publisher"
    const classes = [modelName]
    conversion.for("upcast").elementToElement({
      model: modelName,
      view: {
        name: tagName,
        classes: classes,
      },
    })
    conversion.for("dataDowncast").elementToElement({
      model: modelName,
      view: {
        name: tagName,
        classes: classes,
      },
    })
    conversion.for("editingDowncast").elementToElement({
      model: modelName,
      view: (modelElement, viewWriter) => {
        const element = viewWriter.createEditableElement(tagName, {
          class: classes.join(" "),
        })
        enablePlaceholder({
          view: this.editor.editing.view,
          element: element,
          text: "出版社",
        })
        return toWidgetEditable(element, viewWriter)
      },
    })
  }
}
